import React from 'react';
import PropType from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';
import cookies from 'js-cookie';

import clsx from 'clsx';
import GroupsIcon from '@mui/icons-material/Groups';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import { RouteEnums } from '../../Routes/RoutesConstant';
import useAuthHook, { userRoleEnums } from '../../hooks/custom-hooks/useAuthHook';
import { useWindowSize } from '../../hooks/custom-hooks/useSidebarHook';
import ManageCustomer from '../../assets/svg/ManageCustomer';
import ManageApplication from '../../assets/svg/ManageApplication';
import ManageProject from '../../assets/svg/ManageProject';
import ManageUser from '../../assets/svg/ManageUser';
import ManageRole from '../../assets/svg/ManageRole';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';

function Sidebar(props) {
  const { isSidebarOpen, setIsSidebarOpen, sidebarRef } = props;

  const { pathname } = useLocation();
  const { isCustomerAdmin } = useAuthHook();
  const [width] = useWindowSize();
  const isSettingPage = window.location.pathname === '/widget-setting';

  const mainNavigation = [
    {
      name: 'Manage Customers',
      link: RouteEnums.CUSTOMER,
      icon: <ManageCustomer />,
      allowedRoles: [],
    },
    {
      name: 'Manage Applications',
      link: RouteEnums.APPLICATION,
      icon: <ManageApplication />,
      allowedRoles: [],
    },
    {
      name: 'Manage User Roles',
      link: RouteEnums.USER_ROLE,
      icon: <ManageRole />,
      allowedRoles: [],
    },
    {
      name: 'Manage Projects',
      link: RouteEnums.PROJECT,
      icon: <ManageProject />,
      allowedRoles: [],
    },
    {
      name: 'Manage Users',
      link: RouteEnums.USER,
      icon: <ManageUser />,
      allowedRoles: [userRoleEnums.CUSTOMER_ADMIN],
    },
    {
      name: 'Login Settings',
      link: RouteEnums.LOGIN_SETTING,
      icon: <SettingsIcon color='#003EC4' className='text-[#3b6ad2]' />,
      allowedRoles: [],
    },
    {
      name: 'Notification',
      link: RouteEnums.SEND_NOTIFICATION,
      icon: <NotificationsOutlinedIcon color='#003EC4' className='text-[#3b6ad2]' />,
      allowedRoles: [],
    },
  ];

  const handleNavLinkClick = () => {
    if (width <= 1180) setIsSidebarOpen?.(false);
  };

  return (
    <>
      <section
        ref={sidebarRef}
        className={clsx(
          'z-30 bg-white break:static break:duration-75 ',
          'breakMax:fixed breakMax:top-0 breakMax:h-screen breakMax:w-full breakMax:max-w-[331px]',
          'breakMax:rounded-br-[20px] breakMax:rounded-tr-[20px] breakMax:shadow-lg',
          { 'break:min-w-[290px]': isSettingPage && isSidebarOpen },
          {
            'breakMax:pointer-events-none breakMax:invisible breakMax:!w-0 breakMax:duration-200':
              !isSidebarOpen,
          },
          {
            'breakMax:pointer-events-auto breakMax:visible breakMax:duration-200': isSidebarOpen,
          },
          { 'break:min-w-[250px]': isSidebarOpen },
          { 'break:min-w-[50px]': !isSidebarOpen },
        )}
      >
        <div className='App-sidebar'>
          <div>
            <div
              className={clsx(
                'break:!hidden',
                ' bg-[#200c4c] px-5 pt-16 pb-12 breakMax:relative breakMax:rounded-tr-[20px]',
                { '!hidden': !isSidebarOpen },
                { 'flex items-center gap-3': isSidebarOpen },
              )}
            >
              <p className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-4 border-solid border-[#4b1dae] bg-white text-[#6A2CEF]'>
                {cookies?.get('user')?.split(' ')?.at(0)?.charAt(0)}
              </p>
              <div>
                <p className='text-sm text-white'>{cookies?.get('email')}</p>
                {/* <p className='text-xs text-gray-700'>30 Nov 2022 23:30</p> */}
              </div>
              <div onClick={handleNavLinkClick} className='absolute top-2 right-2'>
                <CloseIcon className='text-white' />
              </div>
            </div>
            <ul>
              {mainNavigation?.map((nav) => {
                if (isCustomerAdmin && !nav?.allowedRoles?.includes(userRoleEnums.CUSTOMER_ADMIN))
                  return;

                return (
                  <li title={nav?.name} key={nav?.name}>
                    <NavLink
                      onClick={handleNavLinkClick}
                      className={({ isActive }) =>
                        clsx(
                          'flex justify-start py-4 font-sans first:mt-2 break:py-4 maclg:py-6',
                          'breakMax:border-none',
                          { 'pl-8': isSidebarOpen },
                          { 'px-4': !isSidebarOpen },
                          {
                            'border-r-[8px] border-solid border-nblue text-base font-semibold text-black':
                              isActive || pathname?.includes(nav.link),
                          },
                          {
                            'text-sm font-medium leading-[25px] text-gray-300': !(
                              isActive || pathname?.includes(nav.link)
                            ),
                          },
                        )
                      }
                      to={nav.link}
                    >
                      <div className='flex items-center justify-between gap-4'>
                        <div
                          className={clsx(
                            { block: !isSidebarOpen },
                            { 'breakMax:hidden': !isSidebarOpen },
                          )}
                        >
                          {nav?.icon || <GroupsIcon className='text-2xl' />}
                        </div>
                        <div
                          className={clsx(
                            'whitespace-nowrap',
                            { block: isSidebarOpen },
                            { hidden: !isSidebarOpen },
                          )}
                        >
                          {nav.name}
                        </div>
                      </div>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>
      <div
        className={clsx('fixed top-0 z-20 h-screen w-full blur backdrop-blur break:hidden', {
          hidden: !isSidebarOpen,
        })}
      />
    </>
  );
}

Sidebar.propTypes = {
  isSidebarOpen: PropType.bool,
  setIsSidebarOpen: PropType.any,
  sidebarRef: PropType.any,
};

Sidebar.defaultProps = {
  isSidebarOpen: true,
  setIsSidebarOpen: undefined,
  sidebarRef: null,
};

export default Sidebar;
